import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Menu, MenuItem, Tooltip, Typography } from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";

import ChannelIcon from "../ChannelIcon";

/**
 * ChannelSwitcherDropdown
 *
 * Phase 3.5 — Channel Switcher inside Conversation
 *
 * Dropdown in the message composer that lets an agent switch to another
 * channel for the same customer without leaving the conversation.
 *
 * Placed to the LEFT of the emoji button in MessageInputCustom.
 *
 * Props:
 *   activeChannel: string — current ticket's channel
 *   channelLinks: Array<{ id, channel, latestTicketId, channelIdentifier, unreadCount }>
 *   onSwitch: (ticketUuid: string) => void — called when agent selects another channel
 */

const CHANNEL_LABELS = {
  whatsapp:  "WhatsApp",
  facebook:  "Messenger",
  instagram: "Instagram",
  email:     "Email",
  telegram:  "Telegram",
};

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  return {
    trigger: {
      display: "inline-flex",
      alignItems: "center",
      gap: 3,
      padding: "4px 8px",
      borderRadius: 8,
      border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid #e2e8f0",
      background: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
      cursor: "pointer",
      userSelect: "none",
      transition: "background 0.15s",
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.08)" : "#f1f5f9",
      },
    },
    channelLabel: {
      fontSize: 11,
      fontWeight: 600,
      color: isDark ? "#e6f7ec" : "#334155",
      whiteSpace: "nowrap",
    },
    arrow: {
      fontSize: 16,
      color: isDark ? "rgba(230,247,236,0.45)" : "#94a3b8",
    },
    menuItem: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 16px",
      minWidth: 180,
    },
    menuItemText: {
      display: "flex",
      flexDirection: "column",
    },
    menuChannel: {
      fontSize: 13,
      fontWeight: 600,
    },
    menuSub: {
      fontSize: 10,
      color: "#94a3b8",
    },
    unread: {
      marginLeft: "auto",
      background: "#ef4444",
      color: "#fff",
      fontSize: 10,
      fontWeight: 700,
      borderRadius: 8,
      padding: "1px 5px",
    },
    activeIndicator: {
      width: 6,
      height: 6,
      borderRadius: "50%",
      background: "#25d366",
      marginLeft: "auto",
    },
  };
});

const ChannelSwitcherDropdown = ({ activeChannel, channelLinks = [], onSwitch }) => {
  const classes = useStyles();
  const [anchor, setAnchor] = useState(null);

  // Only show if there are OTHER channels to switch to
  const otherLinks = channelLinks.filter(
    (l) => l.isActive && l.channel !== activeChannel && l.latestTicketUuid
  );

  if (otherLinks.length === 0) return null;

  return (
    <>
      <Tooltip title="החלף ערוץ">
        <div
          className={classes.trigger}
          onClick={(e) => setAnchor(e.currentTarget)}
          role="button"
        >
          <ChannelIcon channel={activeChannel} size={16} />
          <span className={classes.channelLabel}>
            {CHANNEL_LABELS[activeChannel] || activeChannel}
          </span>
          <ArrowDropDownIcon className={classes.arrow} />
        </div>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{
          style: { minWidth: 200, borderRadius: 12 },
        }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <div style={{ padding: "8px 16px 4px", opacity: 0.6 }}>
          <Typography style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em" }}>
            עבור לערוץ
          </Typography>
        </div>
        {otherLinks.map((link) => (
          <MenuItem
            key={link.id}
            onClick={() => {
              setAnchor(null);
              onSwitch(link.latestTicketUuid);
            }}
            className={classes.menuItem}
          >
            <ChannelIcon channel={link.channel} size={20} />
            <div className={classes.menuItemText}>
              <span className={classes.menuChannel}>
                {CHANNEL_LABELS[link.channel] || link.channel}
              </span>
              {link.channelIdentifier && (
                <span className={classes.menuSub}>{link.channelIdentifier}</span>
              )}
            </div>
            {link.unreadCount > 0 ? (
              <span className={classes.unread}>{link.unreadCount}</span>
            ) : null}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ChannelSwitcherDropdown;
